<template>
  <div class="bg-[#F4F1ED]">
    <!-- メインビジュアルセクション -->
    <MainVisualSectionCMS
      v-if="cmsContent.titleEn && cmsContent.backgroundWideMedias?.length"
      :images="imgUrls"
      :is-sp="isSp"
      :on-button-click="scrollToExperienceGrid"
      :pc-images="pcImageUrls"
      :sp-images="spImageUrls"
      :summary="cmsContent.summary"
      :title-en="cmsContent.titleEn"
      :title-size-preset="titleSizePreset"
    />
    <MainVisualSectionBasic v-else :on-search-click="handleSearchClick" :title="basicTitle" />

    <!-- CMS記事コンテンツセクション -->
    <template v-if="cmsContent.contents?.length">
      <template v-for="(content, index) in cmsContent.contents" :key="`${content.fieldId}-${index}`">
        <!-- Article セクション -->
        <ArticleSection v-if="content?.fieldId === 'articleSection'" :experiences="resolvedArticleExperiences" :section="content" />
        <!-- Story コンテンツセクション -->
        <StoryContentsSection v-else-if="content?.fieldId === 'storyContents'" :section="content" />
        <!-- 体験セクションIDセクション -->
        <template v-else-if="content?.fieldId === 'experienceSectionIDs' && experienceSections?.length">
          <div class="max-w-[1208px] py-8 md:mx-auto md:w-[90%] md:px-8">
            <template v-for="cmsSection in content.experienceSectionIDs" :key="cmsSection.experienceSectionId">
              <ExperienceSection :experience-sections="experienceSectionsByWebId.get(cmsSection.experienceSectionId) ?? []" :should-show-gift-card="false" />
            </template>
          </div>
        </template>
      </template>
    </template>

    <!-- 体験一覧セクション（Discover & Book のスクロール先） -->
    <div ref="experienceGridSectionRef">
      <ExperienceGridSection
        :all-data-initialized="allDataInitialized"
        :destination-tags-query="destinationTagsQuery"
        :experience-count="experienceCount"
        :experience-event-start-at-dates-query="experienceEventStartAtDatesQuery"
        :experiences="experiences"
        :experiences-per-page="experiencesPerPage"
        :is-loading="isLoadingExperiences"
        :map-source-page="mapSourcePage"
        :on-experience-click="handleExperienceClick"
        :on-instant-booking-click="onInstantBookingClick"
        :on-search-click="handleSearchClick"
        :on-show-more-click="onShowMoreClick"
        :on-sort-change="onSortChange"
        :preference-tags-query="preferenceTagsQuery"
        :selected-sort-type="selectedSortType"
        :theme-tags-query="themeTagsQuery"
        :title="gridTitle"
      />
    </div>

    <!-- 旅程一覧セクション -->
    <div v-if="!allDataInitialized || itineraryList.length > 0" id="journey-designed-for-you" :class="itinerarySectionClass">
      <div v-if="allDataInitialized && itineraryList.length > 0">
        <FeaturedSection type="itinerary" :featured-list="itineraryList" :on-featured-click="handleFeaturedClick" />
      </div>
      <!-- ローディング中のプレースホルダー -->
      <div v-else class="animate-pulse bg-[#F4F1ED]">
        <div class="mx-auto max-w-[1208px] px-4 md:px-8">
          <div class="mx-auto mb-8 h-10 w-64 rounded bg-gray-200"></div>
          <div class="grid gap-4 md:grid-cols-3">
            <div class="h-[400px] rounded-lg bg-gray-200"></div>
            <div class="h-[400px] rounded-lg bg-gray-200"></div>
            <div class="h-[400px] rounded-lg bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="cmsContent.contentsBottom?.length">
      <template v-for="(content, index) in cmsContent.contentsBottom" :key="`${content.fieldId}-${index}`">
        <!-- Article セクション -->
        <ArticleSection v-if="content?.fieldId === 'articleSection'" :experiences="resolvedArticleExperiences" :section="content" />
        <!-- Story コンテンツセクション -->
        <StoryContentsSection v-else-if="content?.fieldId === 'storyContents'" :section="content" />
        <!-- 体験セクションIDセクション -->
        <template v-else-if="content?.fieldId === 'experienceSectionIDs' && experienceSections?.length">
          <div class="max-w-[1208px] py-8 md:mx-auto md:w-[90%] md:px-8">
            <template v-for="cmsSection in content.experienceSectionIDs" :key="cmsSection.experienceSectionId">
              <ExperienceSection :experience-sections="experienceSectionsByWebId.get(cmsSection.experienceSectionId) ?? []" :should-show-gift-card="false" />
            </template>
          </div>
        </template>
      </template>
    </template>

    <!-- お問い合わせセクション -->
    <div class="mx-4 mb-4 mt-8 md:mb-14">
      <RequestContact />
    </div>

    <!-- フッターリンクセクション -->
    <ContactLinkSection :on-click-contact="onClickContact" :on-click-partner="onClickPartner" />
  </div>
</template>

<script setup lang="ts">
import { ContactLinkSection } from '@/wabunka-src/common/components';
import { useScrollToSection } from '@/wabunka-src/common/composables/general/useScrollToSection';
import { RequestContact } from '@/wabunka-src/common/features';
import ExperienceGridSection from '@/wabunka-src/common/features/Experience/components/ExperienceGridSection/ExperienceGridSection.vue';
import ExperienceSection from '@/wabunka-src/common/features/Experience/components/ExperienceSection/ExperienceSection.vue';
import type { ExperienceSortType } from '@/wabunka-src/common/features/Experience/utils/experienceListSort';
import { type Contents } from '@/wabunka-src/common/features/Featured';
import ArticleSection from '@/wabunka-src/common/features/Featured/components/ArticleSection/ArticleSection.vue';
import StoryContentsSection from '@/wabunka-src/common/features/Featured/components/StoryContentsSection/StoryContentsSection.vue';
import MainVisualSectionBasic from '@/wabunka-src/common/features/MainVisual/components/MainVisualSectionBasic/MainVisualSectionBasic.vue';
import MainVisualSectionCMS from '@/wabunka-src/common/features/MainVisual/components/MainVisualSectionCMS/MainVisualSectionCMS.vue';
import { extractImageUrls } from '@/wabunka-src/common/utils';
import type { MapSourcePage } from '@/wabunka-src/common/utils/googleAnalytics/common/mapSearch';
import type { ExperienceListViewModel, ExperienceSectionListViewModel } from '@/wabunka-src/features/Experience';
import { FeaturedSection, type MinimumFeaturedListViewModel } from '@/wabunka-src/features/Featured';

import type { MicroCMSImage } from 'microcms-js-sdk';

interface CMSContent {
  titleEn: string;
  backgroundWideMedias: MicroCMSImage[];
  backgroundTallMedias: MicroCMSImage[];
  summary: string;
  contents: Contents;
  contentsBottom?: Contents;
}

interface Props {
  cmsContent: CMSContent;
  destination: string;
  theme?: string;
  basicTitle: string;
  gridTitle: string;
  experiences: ExperienceListViewModel;
  itineraryList: MinimumFeaturedListViewModel;
  isLoadingExperiences: boolean;
  experienceCount: number;
  allDataInitialized: boolean;
  destinationTagsQuery: string;
  themeTagsQuery: string;
  experienceEventStartAtDatesQuery: string;
  experiencesPerPage: number;
  /** GA map_* イベントの source_page（JCAT-4824。どの LP のマップかの内訳に使う） */
  mapSourcePage: MapSourcePage;
  isSp: boolean;
  handleSearchClick: () => void;
  handleExperienceClick: (slug: string, position: number) => void;
  handleFeaturedClick: (type: string, id: string) => void;
  onShowMoreClick: () => void;
  onClickContact: () => void;
  onClickPartner: () => void;
  selectedSortType: ExperienceSortType;
  onSortChange: (sortType: ExperienceSortType) => void;
  preferenceTagsQuery?: string;
  onInstantBookingClick?: () => void;
  /**
   * CMSコンテンツセクション（ArticleSection）で使用する体験リスト。
   * destination / theme フィルターに関係なく、CMS で指定された体験をそのまま表示したい場合に使用する。
   * 指定しない場合は experiences（フィルター済み）にフォールバックする。
   */
  articleSectionExperiences?: ExperienceListViewModel;
  /** CMSの experienceSectionIDs セクションで表示する体験セクションリスト */
  experienceSections?: ExperienceSectionListViewModel;
  /** MainVisualSectionCMS に渡すタイトルサイズプリセット */
  titleSizePreset?: 'default' | 'compact' | 'itinerary' | 'experience' | 'accommodation';
}

const props = withDefaults(defineProps<Props>(), {
  theme: '',
  preferenceTagsQuery: '',
  onInstantBookingClick: undefined,
  articleSectionExperiences: undefined,
  experienceSections: undefined,
  titleSizePreset: 'default',
});

// CMSコンテンツセクションで使用する体験リスト
// フィルターなし取得済み体験を優先しつつ、destinationフィルター済み体験も含めてマージする
// これにより、異なるdestinationの体験もArticleSectionで表示される
const resolvedArticleExperiences = computed(() => {
  const base = props.experiences ?? [];
  if (!props.articleSectionExperiences?.length) {
    return base;
  }
  const existingSlugs = new Set(base.map((e) => e.slug));
  const extras = props.articleSectionExperiences.filter((e) => !existingSlugs.has(e.slug));
  return [...base, ...extras];
});

const experienceGridSectionRef = ref<HTMLElement | null>(null);
const { scrollTo } = useScrollToSection(experienceGridSectionRef);
const scrollToExperienceGrid = () => {
  scrollTo();
};

// Background images from CMS
const imgUrls = computed(() => {
  if (!props.cmsContent.backgroundWideMedias?.length && !props.cmsContent.backgroundTallMedias?.length) {
    return [];
  }
  return props.isSp ? extractImageUrls(props.cmsContent.backgroundTallMedias) : extractImageUrls(props.cmsContent.backgroundWideMedias);
});

// PC用画像URL
const pcImageUrls = computed(() => {
  if (!props.cmsContent.backgroundWideMedias?.length) {
    return [];
  }
  return extractImageUrls(props.cmsContent.backgroundWideMedias);
});

// SP用画像URL
const spImageUrls = computed(() => {
  if (!props.cmsContent.backgroundTallMedias?.length) {
    return [];
  }
  return extractImageUrls(props.cmsContent.backgroundTallMedias);
});

// experienceSectionIDs セクション用に webSectionId → sections のルックアップを事前計算
const experienceSectionsByWebId = computed(() => {
  const map = new Map<string, NonNullable<typeof props.experienceSections>>();
  for (const section of props.experienceSections ?? []) {
    const key = section.webSectionId;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)?.push(section);
  }
  return map;
});

// 旅程セクションのクラス
const itinerarySectionClass = computed(() => {
  if (!props.allDataInitialized || props.itineraryList.length > 0) {
    const minHeight = !props.allDataInitialized ? 'min-h-[600px]' : '';
    return `bg-[#F4F1ED] pt-8 pb-8 md:pt-12 md:pb-2 ${minHeight}`;
  } else {
    return '';
  }
});
</script>
